import React, { useState } from 'react';
import { Group, Text, NumberInput, SegmentedControl, ActionIcon, Tooltip, Select, Paper, SelectProps } from '@mantine/core';
import { IconSettings, IconBan, IconTrash } from '@tabler/icons-react';
import ClickableHelpText from './ClickableHelpText';

export type AggregatorsMapMode = 'counter' | 'percent';

export interface AggregatorsMapValueRange {
  min?: number | null;
  max?: number | null;
}
export interface AggregatorsMapRangeState {
  mode: AggregatorsMapMode;
  values: Record<string, AggregatorsMapValueRange>;
}

const AGGREGATORS = [
  { key: 'ApePro', label: 'ApePro' },
  { key: 'JUP6', label: 'JUP6' },
  { key: 'RayAmmRouter', label: 'Ray Router' },
  { key: 'OKX_Router_v2', label: 'OKX v2' },
];

interface Props {
  value?: AggregatorsMapRangeState;
  onChange: (val: AggregatorsMapRangeState | undefined) => void;
}

// Helper to render SVG icon with fallback
const IconCell: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [err, setErr] = useState(false);
  if (err) return null;
  return (
    <img src={src} alt={alt} width={18} height={18} style={{ display: 'block' }} onError={() => setErr(true)} />
  );
};

const getAggregatorIconSrc = (key: string) => {
  const PUBLIC_URL = process.env.PUBLIC_URL || '';
  return `${PUBLIC_URL}/icons/aggregators/${key}.svg`;
};

// Кастомный рендерер для опций с иконкой (Mantine v7)
const renderOption: SelectProps['renderOption'] = ({ option, checked }) => (
  <Group gap={8} align="center" style={{ width: '100%' }}>
    <IconCell src={getAggregatorIconSrc(option.value)} alt={option.label} />
    <Text size="sm">{option.label}</Text>
    {checked && <span style={{ marginLeft: 'auto', color: '#4F46E5' }}>✓</span>}
  </Group>
);

const AggregatorsMapFilter: React.FC<Props> = ({ value, onChange }) => {
  const mode: AggregatorsMapMode = value?.mode || 'counter';
  const values = value?.values || {};

  const selectedKeys = Object.keys(values);

  const handleProviderChange = (key: string | null) => {
    if (!key) return;
    if (selectedKeys.includes(key)) return;
    onChange({
      mode,
      values: {
        ...values,
        [key]: { min: undefined, max: undefined }
      }
    });
  };

  const handleMinChange = (key: string, min: number | '' | null) => {
    onChange({
      mode,
      values: {
        ...values,
        [key]: { ...values[key], min: min === '' ? undefined : min }
      }
    });
  };

  const handleMaxChange = (key: string, max: number | '' | null) => {
    onChange({
      mode,
      values: {
        ...values,
        [key]: { ...values[key], max: max === '' ? undefined : max }
      }
    });
  };

  const handleModeChange = (newMode: AggregatorsMapMode) => {
    onChange({ mode: newMode, values });
  };

  const handleDefault = () => {
    onChange({
      mode: 'counter',
      values: {
        JUP6: { min: 10, max: 30 }
      }
    });
  };

  const handleClear = () => onChange(undefined);

  // percent validations
  const totalMinPercent = mode === 'percent'
    ? Object.values(values).reduce((acc, v) => acc + (typeof v.min === 'number' ? v.min : 0), 0)
    : 0;
  const percentSumError = mode === 'percent' && totalMinPercent > 100;

  const minMaxErrors: Record<string, boolean> = {};
  selectedKeys.forEach(key => {
    const v = values[key];
    if (
      typeof v?.min === 'number' &&
      typeof v?.max === 'number' &&
      v.min > v.max
    ) {
      minMaxErrors[key] = true;
    }
  });

  return (
    <Paper p="sm" mb="sm" style={{ background: '#181C23', borderRadius: 8, border: '1px solid #23272F' }}>
      <Group justify="space-between" mb={4}>
        <Text fw={500} size="sm">Aggregators</Text>
        <Group gap={6} align="center">
          <ClickableHelpText
            text="Filter by aggregators"
            detailedInfo="Add aggregators and set min/max values. Mode 'count' filters by number of trades, 'percent' filters by percentage of total trades."
          />
          <Tooltip label="Default">
            <ActionIcon size="xs" variant="subtle" onClick={handleDefault}>
              <IconSettings size={14} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Clear">
            <ActionIcon size="xs" variant="subtle" color="red" onClick={handleClear}>
              <IconBan size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {/* SegmentedControl and Select in one row */}
      <Group mb={8} gap={8} align="center" wrap="nowrap">
        <Select
          data={AGGREGATORS.map(p => ({ value: p.key, label: p.label }))}
          onChange={handleProviderChange}
          placeholder="Select aggregator..."
          size="xs"
          value={null}
          nothingFoundMessage="No aggregators"
          renderOption={renderOption}
          styles={{
            input: {
              backgroundColor: '#21262D',
              border: '1px solid #374151',
              color: '#F9FAFB',
              minHeight: '24px',
              fontSize: '12px',
              width: 180,
              maxWidth: 200,
              textAlign: 'left',
              paddingTop: 2,
              paddingBottom: 2,
            },
            dropdown: {
              backgroundColor: '#181C23',
              border: '1px solid #23272F'
            },
            option: {
              backgroundColor: '#181C23',
              color: '#F9FAFB',
              fontSize: '12px',
              '&[dataSelected]': {
                backgroundColor: '#4F46E5'
              },
              '&:hover': {
                backgroundColor: '#21262D'
              }
            }
          }}
        />
        <SegmentedControl
          value={mode}
          onChange={v => handleModeChange(v as AggregatorsMapMode)}
          data={[
            { label: <span style={{ fontSize: 13 }}>count</span>, value: 'counter' },
            { label: <span style={{ fontSize: 13 }}>percent</span>, value: 'percent' }
          ]}
          size="xs"
          style={{ minWidth: 120, flex: 1 }}
        />
      </Group>

      {percentSumError && (
        <Text size="xs" c="red" mb={4}>
          Total percent min cannot exceed 100% (now: {totalMinPercent}%)
        </Text>
      )}

      {selectedKeys.length > 0 && (
        <div>
          {selectedKeys.map(key => {
            const label = AGGREGATORS.find(p => p.key === key)?.label || key;
            const val = values[key] || {};
            const hasMinMaxError = !!minMaxErrors[key];
            return (
              <Group key={key} gap={6} mb={6} align="center" wrap="nowrap">
                <span style={{ display: 'flex', alignItems: 'center', minWidth: 22 }}>
                  <IconCell src={getAggregatorIconSrc(key)} alt={label} />
                </span>
                <Text size="xs" style={{ minWidth: 100, textAlign: 'left' }}>{label}</Text>
                <NumberInput
                  placeholder="Min"
                  value={val.min ?? ''}
                  max={mode === 'percent' ? 100 : undefined}
                  onChange={v => {
                    let parsed: number | '' | null;
                    if (typeof v === 'string') {
                      parsed = v === '' ? '' : Number(v);
                      if (isNaN(parsed as number)) parsed = '';
                    } else {
                      parsed = v;
                    }
                    handleMinChange(key, parsed);
                  }}
                  size="xs"
                  error={hasMinMaxError ? 'Min > Max' : undefined}
                  styles={{
                    input: {
                      backgroundColor: '#181B20',
                      border: '1px solid #23272F',
                      color: '#F9FAFB',
                      borderRadius: 12,
                      fontSize: 14,
                      minHeight: 32,
                      paddingLeft: 12,
                      paddingRight: 12,
                      textAlign: 'center',
                    }
                  }}
                  rightSection={mode === 'percent' ? <span style={{ color: '#aaa', fontSize: 12 }}>%</span> : undefined}
                  hideControls
                />
                <NumberInput
                  placeholder="Max"
                  value={val.max ?? ''}
                  max={mode === 'percent' ? 100 : undefined}
                  onChange={v => {
                    let parsed: number | '' | null;
                    if (typeof v === 'string') {
                      parsed = v === '' ? '' : Number(v);
                      if (isNaN(parsed as number)) parsed = '';
                    } else {
                      parsed = v;
                    }
                    handleMaxChange(key, parsed);
                  }}
                  size="xs"
                  error={hasMinMaxError ? 'Min > Max' : undefined}
                  styles={{
                    input: {
                      backgroundColor: '#181B20',
                      border: '1px solid #23272F',
                      color: '#F9FAFB',
                      borderRadius: 12,
                      fontSize: 14,
                      minHeight: 32,
                      paddingLeft: 12,
                      paddingRight: 12,
                      textAlign: 'center',
                    }
                  }}
                  rightSection={mode === 'percent' ? <span style={{ color: '#aaa', fontSize: 12 }}>%</span> : undefined}
                  hideControls
                />
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="red"
                  onClick={() => {
                    const next = selectedKeys.filter(k => k !== key);
                    const newValues: Record<string, AggregatorsMapValueRange> = {};
                    next.forEach(k => { newValues[k] = values[k]; });
                    onChange({ mode, values: newValues });
                  }}
                  title="Delete"
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>
            );
          })}
        </div>
      )}
    </Paper>
  );
};

export default AggregatorsMapFilter;